// The city chip's hint (site/js/app.js): now and then the name on it rolls through a few other cities, a name at a time like
// a slot machine, and lands back on the city on screen, so a visitor sees that the chip is where cities change. The
// names roll in a strip over the chip's own name, which stays (see-through) for screen readers and as the least width.

/** Shuffles `list` in place (Fisher-Yates) and returns it. */
function shuffle(list, random) {
  for (let i = list.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [list[i], list[j]] = [list[j], list[i]];
  }
  return list;
}

/**
 * `count` of `names` for a roll, in random order, never `current` and never one twice. Names no wider than `max` (by
 * `width`) come first, so the chip grows little; when too few fit, the narrowest of the rest make up the count.
 */
export function rollPicks(names, current, count, { width = () => 0, max = Infinity, random = Math.random } = {}) {
  const pool = shuffle(names.filter(n => n !== current), random);
  const fit = pool.filter(n => width(n) <= max), rest = pool.filter(n => width(n) > max).sort((a, b) => width(a) - width(b));
  return [...fit, ...rest].slice(0, count);
}

const PICKS = 3;              // other cities in a roll
const STEP_MS = 1500;         // one name: it slides in, then holds, so a roll takes about 5 s
const SLIDE = 0.28;           // the part of a step spent sliding

/**
 * Rolls `el` (the chip's name) through PICKS of `names` (rollPicks) and back to `current`, its own. roll(names, current,
 * { grow }) → a promise for the end; the chip widens to each name, by `grow` px at most (the room left in its row),
 * never below its own width. `reduced()` swaps the names without sliding (prefers-reduced-motion). stop() ends a roll
 * at once, e.g. before the name itself changes.
 */
export function createCityRoll(el, { reduced = () => false } = {}) {
  let strip = null, anims = [];
  function stop() {
    for (const a of anims) a.cancel();
    anims = [];
    strip?.remove();
    strip = null;
    el.classList.remove('rolling');
  }
  function roll(all, current, { grow = Infinity } = {}) {
    if (strip) return Promise.resolve();
    const own = el.textContent, box = el.getBoundingClientRect(), h = box.height, w0 = box.width, max = w0 + Math.max(0, grow);
    const ctx = document.createElement('canvas').getContext('2d');
    ctx.font = getComputedStyle(el).font;
    const natural = t => Math.ceil(ctx.measureText(t).width), widthOf = t => Math.max(w0, Math.min(max, natural(t)));
    const picks = rollPicks(all, current, PICKS, { width: natural, max });
    if (!picks.length) return Promise.resolve();
    const names = [...picks, own];
    strip = document.createElement('span');
    strip.className = 'name-roll';
    strip.setAttribute('aria-hidden', 'true');
    for (const t of [own, ...names]) strip.append(Object.assign(document.createElement('span'), { textContent: t }));
    el.append(strip);
    el.classList.add('rolling');
    // One keyframe where each slide starts and one where it ends; the chip's width follows the name coming in.
    const k = names.length, total = (k - 1) * STEP_MS + SLIDE * STEP_MS, ease = reduced() ? 'step-end' : 'cubic-bezier(.45, 0, .2, 1)';
    const moves = [{ offset: 0, transform: 'translateY(0)', easing: ease }], widths = [{ offset: 0, minWidth: `${w0}px`, easing: ease }];
    names.forEach((t, i) => {
      const end = (i * STEP_MS + SLIDE * STEP_MS) / total;
      moves.push({ offset: end, transform: `translateY(${-(i + 1) * h}px)` });
      widths.push({ offset: end, minWidth: `${i < k - 1 ? widthOf(t) : w0}px` });   // back to its own width
      if (i < k - 1) {                                    // hold, then the next slide starts
        const next = (i + 1) * STEP_MS / total;
        moves.push({ offset: next, transform: `translateY(${-(i + 1) * h}px)`, easing: ease });
        widths.push({ offset: next, minWidth: `${widthOf(t)}px`, easing: ease });
      }
    });
    anims = [strip.animate(moves, { duration: total }), el.animate(widths, { duration: total })];
    return anims[0].finished.then(stop, () => {});      // a roll stop() cancelled has already cleaned up
  }
  return { roll, stop };
}
